import type { File, Folder } from '@efdigital/ll-addon-data';
import { DataTypeContent } from '@efdigital/ll-addon-data/data-types';
import { findFolderAndContents } from '@efdigital/ll-admin-v2/data/builders/content/files';
import { parseTypedFileOrFolderId } from '@efdigital/ll-admin-v2/lib/file-icon';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { set } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { Store } from '@warp-drive/core';
import { Request } from '@warp-drive/ember';
import BsModal from 'ember-bootstrap/components/bs-modal';

import FolderBreadcrumb from '../files/folder-breadcrumb.gts';
import { FolderContents } from '../files/folder-contents.gts';
import { FileItem, FolderItem } from '../files/folder-items.gts';

export type FilePickerResult = {
  item: File | Folder;
  type: DataTypeContent.File | DataTypeContent.Folder;
};

type Signature = {
  Args: {
    onSelect?: (result: FilePickerResult) => void;
    canSelectFolders?: boolean;
    canSelectFiles?: boolean;
    clientId?: string;
  };
  Blocks: {
    input: [showModal: (startingPath: string) => void];
  };
};

export class BrowseFolderState {
  @tracked folderId: string = '|';
}

export default class FilePicker extends Component<Signature> {
  @tracked isOpen = false;
  @tracked state = new BrowseFolderState();
  @tracked showClientFolder = true;
  @service declare store: Store;

  navigateToFolder = (item: { id: string | null }) => {
    this.state.folderId = item.id!;
  };

  get clientFolderPath() {
    return this.args.clientId ? `|global|${this.args.clientId}` : null;
  }

  get globalFolderPath() {
    return '|global';
  }

  get hasClientContext() {
    return !!this.args.clientId;
  }

  toggleFolder = () => {
    this.showClientFolder = !this.showClientFolder;
    const newPath = this.showClientFolder
      ? this.clientFolderPath
      : this.globalFolderPath;
    if (newPath) {
      set(this.state, 'folderId', newPath);
    }
  };

  showModal = (startingPath: string) => {
    const { type, id } = parseTypedFileOrFolderId(startingPath);
    let initialPath =
      type === DataTypeContent.Folder
        ? id
        : id.substring(0, id.lastIndexOf('|'));

    // If clientId is provided and starting at root, start at client folder instead
    if (this.hasClientContext && (initialPath === '|' || initialPath === '')) {
      initialPath = this.clientFolderPath!;
      this.showClientFolder = true;
    } else if (this.hasClientContext) {
      // Determine which folder we're in based on the path
      this.showClientFolder = initialPath.startsWith(this.clientFolderPath!);
    }

    this.state.folderId = initialPath;
    this.isOpen = true;
  };

  modalClosed = () => {
    this.isOpen = false;
  };

  get findFolder() {
    return findFolderAndContents(this.state.folderId);
  }

  selectFile = (file: File) => {
    if (!this.args.canSelectFiles) {
      return;
    }

    this.args.onSelect?.({ item: file, type: DataTypeContent.File });
    this.isOpen = false;
  };

  selectFolder = (folder: Folder) => {
    if (!this.args.canSelectFolders) {
      return;
    }

    this.args.onSelect?.({ item: folder, type: DataTypeContent.Folder });
    this.isOpen = false;
  };

  <template>
    {{yield this.showModal to="input"}}
    <BsModal
      @open={{this.isOpen}}
      @onHidden={{this.modalClosed}}
      @size="xl"
      @renderInPlace={{true}}
      as |modal|
    >
      <modal.header>
        <h2 class="modal-title fs-5">Select files</h2>
      </modal.header>
      <modal.body>
        {{#if this.hasClientContext}}
          <div class="mb-3">
            <div class="btn-group" role="group" aria-label="Folder context">
              <button
                type="button"
                class="btn
                  {{if
                    this.showClientFolder
                    'btn-primary'
                    'btn-outline-primary'
                  }}"
                {{on "click" this.toggleFolder}}
                disabled={{this.showClientFolder}}
              >
                Client Folder
              </button>
              <button
                type="button"
                class="btn
                  {{if
                    this.showClientFolder
                    'btn-outline-secondary'
                    'btn-secondary'
                  }}"
                {{on "click" this.toggleFolder}}
                disabled={{if this.showClientFolder false true}}
              >
                Global Folder
              </button>
            </div>
          </div>
        {{/if}}
        <Request @query={{this.findFolder}}>
          <:content as |response|>
            <div class="d-flex justify-content-between align-items-center">
              <FolderBreadcrumb @folderAndContents={{response.data}}>
                <:breadcrumb as |crumb|><button
                    class="btn btn-link p-0"
                    type="button"
                    {{on "click" (fn this.navigateToFolder crumb)}}
                  >{{crumb.name}}</button></:breadcrumb>
              </FolderBreadcrumb>
              {{#if @canSelectFolders}}
                <button
                  class="btn btn-sm btn-primary"
                  type="button"
                  {{on "click" (fn this.selectFolder response.data)}}
                >Select this folder</button>
              {{/if}}
            </div>

            <FolderContents
              @folderAndContents={{response.data}}
              @containerClass="row row-cols-1 row-cols-md-3 row-cols-lg-4 g-3"
            >
              <:folder as |folder|>
                <FolderItem
                  @item={{folder}}
                  @onClick={{this.navigateToFolder}}
                  class="h-100 text-primary border-primary"
                />
              </:folder>
              <:file as |file|>
                <FileItem
                  @item={{file}}
                  @onClick={{this.selectFile}}
                  class="h-100 text-secondary border-secondary"
                />
              </:file>
            </FolderContents>
          </:content>
        </Request>
      </modal.body>
    </BsModal>
  </template>
}
